import { CameraControls, useAnimations } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';

import Lights from '@cartwhl/3d/r3f/components/Lights';
import { Character } from '@cartwhl/common/api/getCharacters';
import Checkbox from '@cartwhl/ui/atoms/Checkbox';
import Slider from '@cartwhl/ui/atoms/Slider';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import useClickOutside from '@cartwhl/ui/hooks/useClickOutside';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import PauseIcon from '@cartwhl/ui/icons/PauseIcon';
import PlayIcon from '@cartwhl/ui/icons/PlayIcon';
import ThreeJSSettingsIcon from '@cartwhl/ui/icons/ThreeJSSettingsIcon';
import Button from '@cartwhl/ui/input/Button';
import CameraButtons from '@cartwhl/ui/molecules/CameraButtons';

import useLoadAnimation from 'hooks/useLoadAnimation';

import { DEMO_ANIMATION_PATH_1 } from '../constants';
import { RetargetingBone } from '../types';
import RetargetingCamera from './RetargetingCamera';
import Skeleton from './Skeleton';

export default function RetargetingPlayer({
  bones,
  character,
  highlightBones,
  scene,
}: {
  bones: RetargetingBone[];
  character: Character;
  highlightBones: string[];
  scene: THREE.Object3D;
}) {
  const [playState, setPlayState] = useState<'stopped' | 'playing' | 'paused'>(
    'stopped',
  );
  const [inputMode, setInputMode] = useState<'rotate' | 'truck' | 'dolly'>(
    'rotate',
  );
  const [opacity, setOpacity] = useState(0.2);
  const [hideBones, setHideBones] = useState(false);
  const [renderBonesInFront, setRenderBonesInFront] = useState(true);
  const [showViewOptions, setShowViewOptions] = useState(false);

  const cameraControlsRef = useRef<CameraControls | null>(null);
  const viewOptionsRef = useRef<HTMLDivElement>(null);
  useClickOutside(viewOptionsRef, () => setShowViewOptions(false));

  const animations = useLoadAnimation([DEMO_ANIMATION_PATH_1], undefined, {
    shouldLoad: true,
    rotationOffsets: character?.rotationOffsets,
  });
  const clips = useMemo(
    () => (animations?.[0] ? [animations[0]?.animationData.clip] : []),
    [animations],
  );

  // Create a unique key that changes when bones change
  const characterKey = useMemo(() => {
    // Create a string from all assigned bone names to generate a key
    return bones
      .filter((bone) => bone.assignedBone)
      .map((bone) => bone.assignedBone?.name)
      .join('_');
  }, [bones]);

  useEffect(() => {
    if (!cameraControlsRef.current) return;
    if (inputMode === 'truck') {
      cameraControlsRef.current.mouseButtons.left = 2;
      cameraControlsRef.current.mouseButtons.right = 1;
      cameraControlsRef.current.mouseButtons.middle = 8;
    } else if (inputMode === 'rotate') {
      cameraControlsRef.current.mouseButtons.left = 1;
      cameraControlsRef.current.mouseButtons.right = 2;
      cameraControlsRef.current.mouseButtons.middle = 8;
    }
  }, [inputMode]);

  return (
    <>
      <Canvas className="rounded-[inherit]">
        <Lights />

        <RetargetingCamera
          highlightBone={
            bones.find(
              (bone) =>
                bone.assignedBone?.userData.originalName ===
                highlightBones.at(-1),
            )?.assignedBone ?? undefined
          }
          hoistCameraControls={(cameraControls) => {
            cameraControlsRef.current = cameraControls;
          }}
        />
        <CharacterPrimitive
          key={`${characterKey}-character`}
          scene={scene}
          clips={clips}
          playState={playState}
        />
        <Skeleton
          key={`${characterKey}-skeleton`}
          object={scene}
          assignedBones={bones
            .map((bone) => bone.assignedBone?.name)
            .filter((name) => name !== undefined)}
          highlightBones={highlightBones}
          meshOpacity={opacity}
          renderOrder={renderBonesInFront ? 1 : -1}
          hideBones={hideBones}
        />
      </Canvas>

      <div className="absolute top-4 right-4 flex gap-2">
        {playState !== 'stopped' && (
          <Button
            variant="secondary"
            icon={<CrossIcon />}
            onClick={() => setPlayState('stopped')}
          />
        )}
        <Button
          variant="primary"
          icon={playState !== 'playing' ? <PlayIcon /> : <PauseIcon />}
          onClick={() =>
            setPlayState((v) => (v === 'playing' ? 'paused' : 'playing'))
          }
        />
      </div>

      <div className="pointer-events-none absolute top-4 right-4 bottom-4 flex items-center">
        <CameraButtons.Root tooltipSide="left" inModal>
          <CameraButtons.InputMode
            inputMode={inputMode}
            setCameraInputMode={setInputMode}
          />
          <CameraButtons.Zoom
            zoomIn={() => cameraControlsRef.current?.dolly(1, true)}
            zoomOut={() => cameraControlsRef.current?.dolly(-1, true)}
          />
          <div className="relative">
            <Tooltip color="default" text="View options" side="left" inModal>
              <Button
                variant="secondary"
                icon={<ThreeJSSettingsIcon />}
                onClick={() => setShowViewOptions(!showViewOptions)}
              />
            </Tooltip>
            {showViewOptions && (
              <div
                className="shadow-menu dark:bg-gray-90 absolute right-12 bottom-0 flex w-[290px] flex-col gap-3 rounded-lg bg-white px-4 py-3"
                ref={viewOptionsRef}
              >
                <Text variant="studio/body/medium/md">View options</Text>
                <Checkbox
                  label="Hide bones"
                  variant="checkbox"
                  color="settings"
                  checked={hideBones}
                  id="hide-bones"
                  setChecked={setHideBones}
                />
                <Checkbox
                  label="Render bones in front"
                  variant="checkbox"
                  color="settings"
                  checked={renderBonesInFront}
                  id="render-bones-in-front"
                  setChecked={setRenderBonesInFront}
                />
                <Slider
                  currentValue={opacity}
                  onChange={(value) => setOpacity(value)}
                  min={0}
                  max={1}
                  step={0.05}
                />
              </div>
            )}
          </div>
        </CameraButtons.Root>
      </div>
    </>
  );
}

const CharacterPrimitive = ({
  scene,
  clips,
  playState,
}: {
  scene: THREE.Object3D;
  clips: THREE.AnimationClip[];
  playState: 'stopped' | 'playing' | 'paused';
}) => {
  const { actions, mixer } = useAnimations(clips, scene);

  useEffect(() => {
    if (!actions.animation || !mixer) return;

    if (playState === 'playing') {
      actions.animation?.play();
      actions.animation.paused = false;
    } else if (playState === 'paused') {
      actions.animation.paused = true;
    } else if (playState === 'stopped') {
      actions.animation.stop();
    }
  }, [actions, playState, mixer]);

  return (
    <primitive
      object={scene}
      position={[0, 0, 0]}
      rotation={playState === 'stopped' ? [0, 0, 0] : [-Math.PI / 2, 0, 0]}
    />
  );
};
